/**
 * ACTIVITY — the session journal and the live activity channel, in words.
 *
 * The journal carries ids and counts (`reduce.ts`), and `reduce.ts` keeps treating every frame as a
 * refetch trigger. But a frame is also an EVENT the reader can be told about: "Set up the build",
 * "Built a component", "Asked you a question". This module is the one table that turns a frame
 * type (and, with `live_frames`, a `turn_activity` code) into a sentence, with a generic fallback
 * for anything this build has never heard of — never a crash, never a claim the server did not make.
 *
 * Live frames (MSA `dialogue/live.py`, contracts 0.2.20): `event: turn_activity`, no `id:` line,
 * `data: {type, turn_id, task_id, n, at, payload:{code, tool, ok, level}}`. Transient: they never
 * move the cursor and are not replayed. `turn_text_delta` (MSA leg `cv-stream`, emitted since
 * 2026-10-05) carries a chunk of the session seat's reply while it is written: it is not an activity
 * row but a LIVE DRAFT (`applyDraftFrame`), shown as a secondary "Writing…" area and replaced by the
 * stored reply (R5) when the turn ends.
 */
import type { SessionFrameWire } from "./types";

export interface LiveFrameWire {
  type: "turn_activity" | "turn_text_delta" | string;
  turn_id: string | null;
  task_id: string | null;
  /** Per-turn, strictly increasing, with gaps for dropped frames. */
  n: number;
  at: string;
  payload: Record<string, unknown>;
}

export interface ActivityRow {
  /** Stable per row: `${source}:${turnId}:${n|seq}`. */
  key: string;
  at: string;
  text: string;
  tone: "ok" | "warn" | "err" | "busy" | "neutral";
  /** `true` once the thing this row names has finished (a built item, a settled check); `false`
   *  while it is in progress; `null` when the frame is a point event. */
  done: boolean | null;
  turnId: string | null;
  taskId: string | null;
  /** Identical consecutive rows fold into one with a count. */
  count: number;
}

/** How many rows the story keeps; older ones are the task's history, which R3 carries anyway. */
export const ACTIVITY_LIMIT = 12;

function str(v: unknown): string | null {
  return typeof v === "string" && v ? v : null;
}
function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Task states as the sentence a transition INTO them means for the reader. */
export const TRANSITION_WORDS: Record<string, string> = {
  exploring: "Exploring your org",
  plan_proposed: "Plan ready for your review",
  contracting: "Preparing the build",
  building: "Building the change",
  amendment_proposed: "Proposed a change to the plan",
  needs_input: "Needs something from you",
  needs_budget: "Needs more budget from you",
  held: "Paused",
  assembling: "Preparing the changes",
  changeset_proposed: "Changes ready for your review",
  checking: "Validating in the org",
  validated: "Ready to save or check again",
  applying: "Applying to the org",
  applied: "Applied to the org",
  cancelled: "Cancelled",
  failed: "Couldn't complete",
};

const TERMINAL_TONE: Record<string, ActivityRow["tone"]> = { applied: "ok", cancelled: "warn", failed: "err" };

/** The sentence for arriving in a task state; an unknown state is still words, never a crash. */
export function transitionWords(to: string): string {
  return TRANSITION_WORDS[to] ?? `Now ${to.replace(/_/g, " ")}`;
}

/**
 * A journal frame as a row, or `null` for frames the reader gains nothing from (grants, graph
 * growth, HITL profiling, bookkeeping). Payload keys are read defensively: every one is optional
 * on the wire and only five frame types are contracted.
 */
export function activityRowForFrame(frame: SessionFrameWire): ActivityRow | null {
  const p = frame.payload ?? {};
  const base = (text: string, tone: ActivityRow["tone"] = "neutral", done: boolean | null = null): ActivityRow => ({
    key: `journal:${frame.seq}`,
    at: frame.at,
    text,
    tone,
    done,
    turnId: frame.run_id ?? str(p.turn_id),
    taskId: frame.task_id,
    count: 1,
  });
  switch (frame.type) {
    case "turn_opened":
      return base("Understanding your request", "busy", false);
    case "turn_ended":
      return null; // the turn's own outcome line says how it ended
    case "ask_raised":
      return base("Asked you a question", "warn", null);
    case "ask_answered":
      return base("You answered", "ok", true);
    case "ask_withdrawn":
      return base("Withdrew a question", "neutral", true);
    case "ask_expired":
      return base("A question expired", "warn", true);
    case "task_transitioned": {
      const to = str(p.to);
      if (!to) return null;
      const words = transitionWords(to);
      const terminal = TERMINAL_TONE[to];
      return base(words, terminal ?? "busy", terminal ? true : false);
    }
    case "plan_proposed":
      return base(str(p.origin) === "agent_amendment" ? "Proposed a plan change" : "Drafted a plan", "ok", true);
    case "plan_revised":
      return base("Plan updated", "ok", true);
    case "build_materialized": {
      const n = num(p.item_count);
      return base(n ? `Set up the build (${n} ${n === 1 ? "item" : "items"})` : "Set up the build", "ok", true);
    }
    case "item_built":
      return base("Built a component", "ok", true);
    case "item_failed":
      return base("A component failed to build", "err", true);
    case "level_done":
      return base("Finished a build level", "ok", true);
    case "level_check_submitted":
      return base("Checking a build level in the org", "busy", false);
    case "level_check_settled":
      return p.ok === false ? base("A build level check failed", "err", true) : base("Build level check passed", "ok", true);
    case "build_parked":
      return base("Paused the build for your input", "warn", true);
    case "build_terminal": {
      const reason = str(p.reason);
      if (reason === "failed") return base("The build failed", "err", true);
      if (reason === "cancelled") return base("The build was cancelled", "warn", true);
      return base("Build finished", "ok", true);
    }
    case "deviation_proposed":
      return base("Proposed a change to the plan", "warn", null);
    case "package_assembled": {
      const n = num(p.member_count);
      return base(n ? `Prepared ${n} ${n === 1 ? "component" : "components"}` : "Prepared the changes", "ok", true);
    }
    case "check_finished":
      return p.ok === false ? base("Validation failed", "err", true) : base("Validation passed", "ok", true);
    case "apply_finished":
      return p.ok === false ? base("Applying to the org failed", "err", true) : base("Applied to the org", "ok", true);
    case "drift_detected":
      return base("The org changed meanwhile; re-checking", "warn", null);
    case "reverted":
      return base("Reverted", "ok", true);
    case "branch_saved":
      return base("Saved to your branch", "ok", true);
    // MSA leg `cv-connector-writes`: a write to a connected external tool (Notion, Jira, …) drafted for approval, then
    // run after the click. The result row names the tool and the page; the link is the canonical record url.
    case "connector_write_drafted": {
      const provider = str(p.provider) || "a connected tool";
      return base(`Drafted a write to ${provider} for your approval`, "warn", null);
    }
    case "connector_write_done": {
      const provider = str(p.provider) || "the connected tool";
      if (p.ok === false) return base(`The write to ${provider} failed`, "err", true);
      const title = str(p.title);
      const url = str(p.url);
      const what = title ? `“${title}”` : "the record";
      // MSA leg `notion-e2e`: an attach names the file it put on the page
      if (str(p.action) === "attach") return base(`Attached ${title ? what : "the file"} in ${provider}${url ? ` — ${url}` : ""}`, "ok", true);
      return base(`Wrote ${what} to ${provider}${url ? ` — ${url}` : ""}`, "ok", true);
    }
    default:
      return null;
  }
}

/** `turn_activity` codes (MSA `dialogue/live.py` `ActivityCode`) as sentences. Open set. */
const ACTIVITY_CODE_WORDS: Record<string, string> = {
  searching_metadata: "Searching metadata",
  reading_component: "Reading a component",
  searching_source: "Searching source",
  reading_source: "Reading source",
  checking_org: "Checking the org",
  searching_graph: "Searching the dependency graph",
  tracing_dependencies: "Tracing dependencies",
  tracing_usage: "Tracing usage",
  searching_docs: "Searching documentation",
  reading_attachment: "Reading your attachment",
  // MSA leg `notion-e2e` (the server sends its own sentence; these are the fallback)
  searching_connected_tool: "Searching your connected tool",
  reading_connected_tool: "Reading from your connected tool",
  listing_metadata: "Listing metadata",
  finding_reusable: "Looking for reusable components",
  surveying_footprint: "Surveying the footprint",
  reviewing_build_results: "Reviewing build results",
  writing_reply: "Writing the reply",
  plan_written: "Plan written",
  asking_question: "Preparing a question",
  plan_drafting: "Drafting the plan",
  plan_coverage: "Checking plan coverage",
  plan_review: "Reviewing the plan",
  plan_org_check: "Checking the plan against your org",
  plan_settling: "Finalising the plan",
  plan_contracts: "Preparing the build contracts",
  working: "Working on your request",
};

export const ACTIVITY_FALLBACK = "Working on your request";

/** Most characters of a live draft kept; the tail wins (the newest words are the ones being written). */
export const LIVE_DRAFT_MAX_CHARS = 4000;

/** The reply as it is being written, for one turn. Never a second truth: R5's reply replaces it. */
export interface LiveDraft {
  turnId: string;
  /** Model round the text belongs to; a later round replaces an earlier one (text before a tool call is a draft). */
  round: number;
  text: string;
}

/** Fold a `turn_text_delta` frame into the draft; any other frame leaves it untouched. */
export function applyDraftFrame(prev: LiveDraft | null, frame: LiveFrameWire): LiveDraft | null {
  if (frame.type !== "turn_text_delta" || !frame.turn_id) return prev;
  const p = frame.payload ?? {};
  const chunk = typeof p.text === "string" ? p.text : "";
  if (!chunk) return prev;
  const round = typeof p.round === "number" && Number.isFinite(p.round) ? p.round : 0;
  let text = chunk;
  if (prev && prev.turnId === frame.turn_id) {
    if (round < prev.round) return prev;
    if (round === prev.round) text = prev.text + chunk;
  }
  if (text.length > LIVE_DRAFT_MAX_CHARS) text = text.slice(text.length - LIVE_DRAFT_MAX_CHARS);
  return { turnId: frame.turn_id, round, text };
}

/** A live `turn_activity` frame as a row; `null` for `turn_text_delta` and unknown types. */
export function activityRowForLive(frame: LiveFrameWire): ActivityRow | null {
  if (frame.type !== "turn_activity") return null;
  const p = frame.payload ?? {};
  const code = str(p.code);
  // 0.2.21 pre-wires `payload.text`, the server's own sentence; prefer it when present.
  const text = str(p.text) ?? (code ? ACTIVITY_CODE_WORDS[code] ?? ACTIVITY_FALLBACK : ACTIVITY_FALLBACK);
  const ok = p.ok;
  return {
    key: `live:${frame.turn_id ?? ""}:${frame.n}`,
    at: frame.at,
    text,
    tone: ok === false ? "err" : ok === true ? "ok" : "busy",
    done: ok === true || ok === false ? true : false,
    turnId: frame.turn_id,
    taskId: frame.task_id,
    count: 1,
  };
}

/**
 * Append a row, folding an identical consecutive sentence into a count ("Built a component ×4") and
 * settling a `done: false` row when its finish arrives with the same words. Keeps the newest
 * `ACTIVITY_LIMIT`.
 */
export function appendActivity(rows: readonly ActivityRow[], next: ActivityRow): ActivityRow[] {
  const last = rows[rows.length - 1];
  if (last && last.text === next.text && last.turnId === next.turnId) {
    const merged: ActivityRow = {
      ...last,
      at: next.at,
      tone: next.tone,
      done: next.done ?? last.done,
      count: next.done === true && last.done === false ? last.count : last.count + 1,
    };
    return [...rows.slice(0, -1), merged];
  }
  // A row still "in progress" is over the moment a different thing starts: the agent moved on.
  const settled = last && last.done === false ? [...rows.slice(0, -1), { ...last, done: true, tone: last.tone === "busy" ? "neutral" : last.tone }] : rows;
  const out = [...settled, next];
  return out.length > ACTIVITY_LIMIT ? out.slice(out.length - ACTIVITY_LIMIT) : out;
}
